/**
 * Flinnt cart drawer countdown timer.
 *
 * A <flinnt-cart-timer data-minutes="10"> web component that counts down inside
 * the cart drawer. The deadline is persisted in localStorage so the countdown
 * keeps running across drawer opens/closes and page reloads, and resets neatly
 * when it expires (loops to a fresh full duration) or when the cart is emptied.
 *
 * Registration lives in this asset module (loaded once via header-actions.liquid)
 * rather than an inline script, so the custom element is defined before the
 * drawer markup appears via section morphing on the first add-to-cart.
 */
(function () {
  var STORAGE_KEY = 'flinnt:cartTimerDeadline';

  function readDeadline() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var value = parseInt(raw, 10);
      return isNaN(value) ? null : value;
    } catch (e) {
      return null;
    }
  }

  function writeDeadline(value) {
    try { localStorage.setItem(STORAGE_KEY, String(value)); } catch (e) { /* private mode */ }
  }

  function clearDeadline() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* private mode */ }
  }

  // Reset the countdown whenever the cart becomes empty, regardless of whether a
  // timer element is currently mounted. Runs once at module scope.
  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    var count;
    if (detail.resource && typeof detail.resource.item_count === 'number') {
      count = detail.resource.item_count;
    } else if (detail.data && typeof detail.data.itemCount === 'number') {
      count = detail.data.itemCount;
    }
    if (count === 0) clearDeadline();
  });

  var FlinntCartTimer = (function () {
    function FlinntCartTimer() {
      return Reflect.construct(HTMLElement, [], FlinntCartTimer);
    }

    FlinntCartTimer.prototype = Object.create(HTMLElement.prototype);
    FlinntCartTimer.prototype.constructor = FlinntCartTimer;
    Object.setPrototypeOf(FlinntCartTimer, HTMLElement);

    FlinntCartTimer.prototype.connectedCallback = function () {
      this.display = this.querySelector('[data-timer-display]');
      var minutes = parseInt(this.getAttribute('data-minutes'), 10);
      if (isNaN(minutes) || minutes < 1) minutes = 10;
      this.durationMs = minutes * 60 * 1000;
      this.start();
    };

    FlinntCartTimer.prototype.disconnectedCallback = function () {
      this.stop();
    };

    // Return the active deadline, creating a fresh one when missing or expired.
    FlinntCartTimer.prototype.deadline = function () {
      var value = readDeadline();
      var now = Date.now();
      if (value === null || value <= now) {
        value = now + this.durationMs;
        writeDeadline(value);
      }
      return value;
    };

    FlinntCartTimer.prototype.start = function () {
      this.stop();
      this.tick();
      var self = this;
      this.interval = setInterval(function () { self.tick(); }, 1000);
    };

    FlinntCartTimer.prototype.stop = function () {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    };

    FlinntCartTimer.prototype.tick = function () {
      var remaining = this.deadline() - Date.now();
      var totalSeconds = Math.max(0, Math.round(remaining / 1000));
      var minutes = Math.floor(totalSeconds / 60);
      var seconds = totalSeconds % 60;
      if (this.display) {
        this.display.textContent = minutes + ':' + (seconds < 10 ? '0' + seconds : seconds);
      }

      // Draining progress bar (100% -> 0%), clamped in case the configured
      // duration changed while an older deadline is still active.
      var fraction = Math.min(1, Math.max(0, remaining / this.durationMs));
      this.style.setProperty('--cart-timer-progress', (fraction * 100).toFixed(2) + '%');

      // Urgent state during the last minute.
      if (totalSeconds <= 60) {
        this.setAttribute('data-urgent', '');
      } else {
        this.removeAttribute('data-urgent');
      }
    };

    return FlinntCartTimer;
  })();

  if (!customElements.get('flinnt-cart-timer')) {
    customElements.define('flinnt-cart-timer', FlinntCartTimer);
  }
})();
